import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ItemResponseModelV2, SupplierV2 } from '../models/item-response-model-v2';
import { ItemsService } from '../items.service';
import { PersistMessageReferenceService } from '../persist-message-reference.service';
import { ItemSearchResponseModel } from '../models/item-search-response-model';
import { EnGbTextPipe } from '../../utils/en-gb-text.pipe';
import { ItemReplacedHistoryResponseModel } from '../models/item-replaced-history-response-model';
import { ItemPropertyModel } from '../models/item-property-model';
import { TabModel } from 'src/app/models/tab-model';
import { ViewSupplierComponent } from './view-supplier.component';
import { ViewMessageRefComponent } from './view-messageref.component';
import { ViewComparisonComponent } from './view-comparison.component';
import { EditItemPropertyComponent } from './edit-item-property.component';
import { ItemPropertiesService } from '../item-properties.service';
import { ConfirmDeleteComponent } from './confirm-delete.component';
import { AuthService } from 'src/app/auth.service';
import { ExportControl } from '../models/export-control.model';
import { PersistMessageReferenceResponseModel } from '../models/persist-message-reference-response-model';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { UtilService } from 'src/app/common-services/util.service';

@Component({
    selector: 'view-item',
    standalone: true,
    imports: [CommonModule, FormsModule, NgbModule, NgbNavModule, KENDO_GRID, EnGbTextPipe],
    providers: [ItemsService, PersistMessageReferenceService],
    templateUrl: './view-item.component.html'
})
export class ViewItemComponent implements OnInit {  
  @Input() itemModel: ItemSearchResponseModel;
  public itemResponseModel: ItemResponseModelV2 = null;
  public rowDefs: SupplierV2[] = [];
  public suppliersLoading: boolean = true;
  public suppliersError: string = '';
  public itemPropertiesRowDefs: ItemPropertyModel[] = [];
  public historyRowDefs: ItemReplacedHistoryResponseModel[] = [];
  public exportControlRowDefs: ExportControl[] = [];
  public exportControlLoading: boolean = false;
  public exportControlError: string = '';
  public descriptionRowDefs: { languageCode: string; description: string; detailedDescription: string }[] = [];
  public persistMessageReferencesRowDefs: GridDataResult = { data: [], total: 0 };
  public persistMessageReferencesLoading: boolean = false;
  public persistMessageReferencesError: string = '';
  public persistMessageReferencesPageIndex: number = 0;
  public persistMessageReferencesPageSize: number = 10;
  public persistMessageSelectedIds: number[] = [];
  public tabModel: TabModel = new TabModel();
  public showSupplierTab: boolean = false;
  public showHistoryTab: boolean = false;
  public isAdmin: boolean = false;
  public isReader: boolean = false;
  
  constructor(
    private itemService: ItemsService,
    private persistMessageReferenceService: PersistMessageReferenceService, 
    private authService: AuthService, 
    public activeModal: NgbActiveModal, 
    private modalService: NgbModal,
    private itemPropertiesService: ItemPropertiesService,
    public utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.getData();

    this.authService.getRoles().subscribe({
      next: (roles) => {
        this.isAdmin = roles.includes('ItemRegistry.Admin');
        this.isReader = roles.includes('ItemRegistry.Read');
        this.showSupplierTab = true;
      },
      error: (error) => {
        console.error('Error loading roles:', error);
        this.isAdmin = false;
        this.isReader = false;
      }
    });
  }

  public close() {
      this.activeModal.close('close');
  }

  private getData() {
    this.suppliersLoading = true;
    this.suppliersError = '';
    this.itemService.getItemv2(this.itemModel.itemId).subscribe(
    {
      next: (result) => {
        this.itemResponseModel = result;
        this.rowDefs = result.suppliers ?? []; 
        this.suppliersLoading = false;
        this.getItemPropertiesData();
        this.getHistoryData();
        this.buildDescriptionRowDefs();
      },
      error: (error) => {
        console.log(`error: ` + error);
        this.suppliersLoading = false;
        this.suppliersError = error?.message || 'Failed to load suppliers';
      }
    });
  }

  private getItemPropertiesData() {
    if (this.itemResponseModel && this.itemResponseModel.itemProperties) {
      this.itemPropertiesRowDefs = this.itemResponseModel.itemProperties;
    }
  }

  private buildDescriptionRowDefs() {
    const details = this.itemResponseModel?.itemDetails;
    const codes = new Set<string>(
      [...(details?.description ?? []), ...(details?.detailedDescription ?? [])].map(t => t.languageCode)
    );
    this.descriptionRowDefs = Array.from(codes).sort().map(code => ({
      languageCode: code,
      description: details?.description?.find(t => t.languageCode === code)?.value ?? '',
      detailedDescription: details?.detailedDescription?.find(t => t.languageCode === code)?.value ?? ''
    }));
  }

  private getHistoryData() {    if (this.itemResponseModel && this.itemResponseModel.history) {
      this.historyRowDefs = this.itemResponseModel.history.map<ItemReplacedHistoryResponseModel>((v: ItemReplacedHistoryResponseModel) : ItemReplacedHistoryResponseModel => ({ 
        ...v, 
        eventDateTime: this.formatDateTime(v.eventDateTime),
        createdDateTime: this.formatDateTime(v.createdDateTime)
      }));
    }
  }

  private formatDateTime(datetime: string): string {
    if(datetime != undefined) {
      return datetime.replace('T', ' ');
    } else {
      return '';
    }
  }

  public viewSupplier(gridRow: SupplierV2) {
    const modalRef = this.modalService.open(ViewSupplierComponent, { size: 'size_60' });
    modalRef.componentInstance.supplierModel = gridRow;

    modalRef.result.then((result) => {
        // No action needed for view modal
    }, (reason) => {
        // User clicked outside modal window => do nothing (same as cancel)
    });
  }

  public onclickGoToTab(tab: number) {
    this.tabModel.activeTab = tab;
    if (tab === 4) {
      this.getExportControlData();
    }
    if (tab === 5) {
      this.getPersistMessageReferencesData();
    }
  }

  private getExportControlData() {
    if (!this.itemModel?.itemId) return;
    this.exportControlLoading = true;
    this.exportControlError = '';
    this.itemService.getExportControls(this.itemModel.itemId).subscribe({
      next: (result) => {
        this.exportControlRowDefs = result || [];
        this.exportControlLoading = false;
      },
      error: (error) => {
        this.exportControlLoading = false;
        this.exportControlError = error?.message || 'Failed to load export control data';
        console.error('Error loading export control data:', error);
      }
    });
  }

  private getPersistMessageReferencesData() {
    if (!this.itemModel?.itemId) return;
    this.persistMessageReferencesLoading = true;
    this.persistMessageReferencesError = '';
    this.persistMessageReferenceService.getPersistMessageReferences(this.itemModel.itemId, this.persistMessageReferencesPageIndex, this.persistMessageReferencesPageSize).subscribe({
      next: (result) => {
        this.persistMessageReferencesRowDefs = result;
        this.persistMessageReferencesLoading = false;
      },
      error: (error) => {
        this.persistMessageReferencesLoading = false;
        this.persistMessageReferencesError = error?.message || 'Failed to load message references';
        console.error('Error loading persist message references:', error);
      }
    });
  }

  public onPersistMessageReferencesPageChange(event: PageChangeEvent) {
    this.persistMessageReferencesPageIndex = event.skip;
    this.persistMessageReferencesPageSize = event.take;
    this.getPersistMessageReferencesData();
  }

  public isPersistMessageSelected(id: number): boolean {
    return this.persistMessageSelectedIds.includes(id);
  }

  public onPersistMessageCheckChange(id: number, checked: boolean): void {
    if (checked) {
      if (this.persistMessageSelectedIds.length < 2) {
        this.persistMessageSelectedIds = [...this.persistMessageSelectedIds, id];
      }
    } else {
      this.persistMessageSelectedIds = this.persistMessageSelectedIds.filter(x => x !== id);
    }
  }

  public get canCompareSelectedMessages(): boolean {
    return this.persistMessageSelectedIds.length === 2;
  }

  public viewMessageReference(row: PersistMessageReferenceResponseModel) {
    const modalRef = this.modalService.open(ViewMessageRefComponent, { size: 'lg' });
    modalRef.componentInstance.row = row;
  }

  public compareSelectedMessages(): void {
    const rows: PersistMessageReferenceResponseModel[] = this.persistMessageReferencesRowDefs.data;
    const selected = this.persistMessageSelectedIds.map(id => rows.find(r => r.id === id)).filter(r => !!r);
    const [newerItem, olderItem] = selected.sort((a, b) =>
      new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime()
    );
    const modalRef = this.modalService.open(ViewComparisonComponent, { size: 'lg' });
    modalRef.componentInstance.currentId = newerItem.id;
    modalRef.componentInstance.previousId = olderItem.id;
  }

  public get replacedBy(): string {
    if (!this.itemResponseModel || !this.itemResponseModel.history) return '';
    const match = this.itemResponseModel.history.find(h => h.itemId === this.itemResponseModel.itemDetails.itemId);
    return match?.replacedByItemId || '';
  }

  public get supersedesFromHistory(): string[] {
    if (!this.itemResponseModel || !this.itemResponseModel.history) return [];
    const history = this.itemResponseModel.history;
    const result: string[] = [];
    const queue: string[] = [this.itemResponseModel.itemDetails.itemId];

    while (queue.length > 0) {
      const id = queue.shift();
      const predecessors = history
        .filter(h => h.replacedByItemId === id)
        .map(h => h.itemId);
      for (const pred of predecessors) {
        if (!result.includes(pred)) {
          result.push(pred);
          queue.push(pred);
        }
      }
    }

    return result;
  }

  addNewItemProperty() {
    const modalRef = this.modalService.open(EditItemPropertyComponent, { size: 'lg' });
    modalRef.componentInstance.isNewProperty = true;
    modalRef.componentInstance.itemId = this.itemModel.itemId;

    modalRef.result.then((result) => {
      if (result.action === 'save') {
        this.itemPropertiesRowDefs = [...this.itemPropertiesRowDefs, result.data];
        // TODO: Save to backend
        // this.itemService.addItemProperty(this.itemModel.itemId, result.data).subscribe(...);
      }
    }, (reason) => {
      // User dismissed the modal
    });
  }

  editItemProperty(gridRow: ItemPropertyModel) {
    const modalRef = this.modalService.open(EditItemPropertyComponent, { size: 'lg' });
    modalRef.componentInstance.isNewProperty = false;
    modalRef.componentInstance.itemProperty = gridRow;

    modalRef.result.then((result) => {
      if (result.action === 'save') {
        // Update the row in the grid
        const index = this.itemPropertiesRowDefs.findIndex(p => 
          p.key === gridRow.key && p.value === gridRow.value
        );
        if (index !== -1) {
          this.itemPropertiesRowDefs[index] = result.data;
          this.itemPropertiesRowDefs = [...this.itemPropertiesRowDefs];
        }
        // TODO: Save to backend
        // this.itemService.updateItemProperty(this.itemModel.itemId, result.data).subscribe(...);
      }
    }, (reason) => {
      // User dismissed the modal
    });
  }

  deleteItemProperty(gridRow: ItemPropertyModel) {
    const modalRef = this.modalService.open(ConfirmDeleteComponent);
    modalRef.componentInstance.title = 'Confirm Delete';
    modalRef.componentInstance.message = `Are you sure you want to delete the property "${gridRow.key}"?`;
    
    modalRef.result.then((result) => {
      if (result === 'confirmed') {
        this.itemPropertiesService.deleteItemProperty(gridRow.itemId, gridRow.key).subscribe({
          next: () => {
            // Remove the row from the grid
            this.itemPropertiesRowDefs = this.itemPropertiesRowDefs.filter(p => 
              !(p.itemId === gridRow.itemId && p.key === gridRow.key)
            );
            console.log('Item property deleted successfully');
          },
          error: (error) => {
            console.error('Error deleting item property:', error);
            alert('Failed to delete the property. Please try again.');
          }
        });
      }
    }, (reason) => {
      // User cancelled or dismissed the modal
    });
  }

}
